/**
 * The wagon wheel on Match Detail (ADR-0022): where a match's runs were scored, by batter and by
 * value of shot.
 *
 * Reads only `state.innings[].deliveries`, which `replay()` already folded, and hands the rest to
 * `wagonWheel()` in the engine. Nothing is stored or summed here, so the card cannot disagree
 * with the scorecard or with the public page, which draws from the same function.
 *
 * Absent entirely when no shot was recorded. A match scored with the wheel off, which is every
 * match scored before this existed, gets no empty card and no prompt to use it.
 */
import { useMemo, useState } from "react";
import { wagonWheel, ZONES } from "@sportsos/engine";
import type { InningsState, MatchState, PlayerId } from "@sportsos/engine";
import { WagonGround, WagonKey } from "./WagonWheel.tsx";

interface Props {
  readonly state: MatchState;
  readonly name: (playerId: PlayerId) => string;
  readonly teamName: (teamId: InningsState["battingTeamId"]) => string;
}

const RUN_FILTERS = [null, 1, 2, 3, 4, 6] as const;

const placedIn = (innings: InningsState): number =>
  innings.deliveries.filter((ball) => ball.shot !== null && ball.runs.batter > 0).length;

export function WagonWheelCard({ state, name, teamName }: Props): React.ReactElement | null {
  const withShots = state.innings.filter((innings) => placedIn(innings) > 0);
  const [chosen, setChosen] = useState<number | null>(null);
  const [batter, setBatter] = useState<PlayerId | null>(null);
  const [runs, setRuns] = useState<number | null>(null);

  const innings =
    withShots.find((entry) => entry.inningsNo === chosen) ?? withShots[withShots.length - 1];

  // Hooks run before the early return below, so the card is cheap when there is nothing to show.
  const wheel = useMemo(
    () =>
      innings === undefined
        ? null
        : wagonWheel(innings.deliveries, {
            ...(batter === null ? {} : { batterId: batter }),
            ...(runs === null ? {} : { runs }),
          }),
    [innings, batter, runs],
  );

  if (innings === undefined || wheel === null) return null;

  const everyone = wagonWheel(innings.deliveries);
  const batters = [...new Set(everyone.lines.map((line) => line.batterId))];
  // A batter chosen in one innings is not a batter in another: fall back to everyone.
  const batterId = batter !== null && batters.includes(batter) ? batter : null;
  const hand = batterId === null ? null : (wheel.lines[0]?.hand ?? everyone.lines.find((l) => l.batterId === batterId)?.hand ?? "right");
  const mixed = batterId === null && new Set(wheel.lines.map((line) => line.hand)).size > 1;
  const hottest = Math.max(1, ...wheel.runsByZone);
  const scoringShots = wheel.lines.length + wheel.unplaced;

  const chip = (pressed: boolean, label: string, onClick: () => void): React.ReactElement => (
    <button
      key={label}
      type="button"
      className="ds-chip"
      aria-pressed={pressed}
      onClick={onClick}
    >
      {label}
    </button>
  );

  return (
    <article className="ds-card" style={{ padding: "var(--ds-space-4) var(--ds-space-5)", marginTop: "var(--ds-space-3)" }}>
      <div className="ds-title" style={{ marginBottom: "var(--ds-space-3)" }}>Wagon wheel</div>

      {withShots.length > 1 ? (
        <div className="row" style={{ flexWrap: "wrap", marginBottom: "var(--ds-space-2)" }}>
          {withShots.map((entry) =>
            chip(entry.inningsNo === innings.inningsNo, teamName(entry.battingTeamId), () => {
              setChosen(entry.inningsNo);
              setBatter(null);
            }),
          )}
        </div>
      ) : null}

      <div className="row" style={{ flexWrap: "wrap", marginBottom: "var(--ds-space-2)" }}>
        {chip(batterId === null, "All batters", () => setBatter(null))}
        {batters.map((id) => chip(batterId === id, name(id), () => setBatter(id)))}
      </div>
      <div className="row" style={{ flexWrap: "wrap", marginBottom: "var(--ds-space-3)" }}>
        {RUN_FILTERS.map((value) =>
          chip(runs === value, value === null ? "All runs" : `${value}s`, () => setRuns(value)),
        )}
      </div>

      {wheel.lines.length === 0 ? (
        <p className="ds-caption">No shots with these filters.</p>
      ) : (
        <>
          <div style={{ maxWidth: 320, margin: "0 auto" }}>
            <WagonGround lines={wheel.lines} hand={hand} shade title="Wagon wheel" />
          </div>
          <div style={{ margin: "var(--ds-space-2) 0 var(--ds-space-3)" }}><WagonKey /></div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "var(--ds-space-2)" }}>
            <Stat value={String(wheel.totalRuns)} label="runs placed" />
            <Stat value={wheel.offPercent === null ? "–" : `${wheel.offPercent}%`} label="off side" />
            <Stat value={`${wheel.fours} · ${wheel.sixes}`} label="4s · 6s" />
          </div>

          <table style={{ width: "100%", marginTop: "var(--ds-space-3)", borderCollapse: "collapse", fontVariantNumeric: "tabular-nums" }}>
            <tbody>
              {ZONES.map((zone, index) => ({ zone, total: wheel.runsByZone[index] ?? 0 }))
                .filter((row) => row.total > 0)
                .sort((a, b) => b.total - a.total)
                .map((row) => (
                  <tr key={row.zone} style={{ borderBottom: "1px solid var(--ds-border)" }}>
                    <td style={{ padding: "6px 0", width: "34%" }}>{row.zone}</td>
                    <td style={{ padding: "6px var(--ds-space-2)" }}>
                      <div style={{ height: 6, borderRadius: 3, background: "var(--ds-accent)", width: `${Math.round((row.total * 100) / hottest)}%` }} />
                    </td>
                    <td style={{ padding: "6px 0", textAlign: "right", width: "14%", fontWeight: 600 }}>{row.total}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </>
      )}

      <p className="ds-caption" style={{ marginTop: "var(--ds-space-3)" }}>
        {wheel.unplaced > 0
          ? `${wheel.lines.length} of ${scoringShots} scoring shots placed. `
          : ""}
        {mixed ? "Left-handers' shots are mirrored so every batter reads as a right-hander. " : ""}
        Byes, leg byes and wides are not shown.
      </p>
    </article>
  );
}

function Stat({ value, label }: { readonly value: string; readonly label: string }): React.ReactElement {
  return (
    <div style={{ background: "var(--ds-surface-sunken)", borderRadius: "var(--ds-radius-sm)", padding: "var(--ds-space-2) var(--ds-space-3)" }}>
      <b style={{ display: "block", fontSize: "var(--ds-text-lg)", fontVariantNumeric: "tabular-nums" }}>{value}</b>
      <span className="ds-caption">{label}</span>
    </div>
  );
}
